import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiMoreVertical } from 'react-icons/fi';
import usePopoverAnclado from '../../hooks/usePopoverAnclado';
import '../../css/menu-acciones.css';

/**
 * Menú de acciones de una fila: botón de tres puntos que abre una lista.
 *
 * El anclaje y el cierre (fuera, Escape, scroll) los pone
 * `usePopoverAnclado`, que es lo mismo que usan los selectores de filtros.
 *
 * Cada acción es `{ clave, etiqueta, icono, onSelect, peligrosa, separada,
 * deshabilitada, descripcion }`. El componente no sabe qué hacen: quien lo usa
 * decide si abren un modal, navegan o copian algo.
 *
 * Accesible: `aria-haspopup="menu"`, flechas para recorrer, Inicio/Fin,
 * Escape para cerrar y el foco vuelve al botón al cerrarse.
 */
const ANCHO = 230;

const MenuAcciones = ({ acciones, etiqueta, titulo, alAbrir }) => {
  const { t } = useTranslation();
  const [indiceActivo, setIndiceActivo] = useState(-1);
  const { abierto, alternar, cerrar, refBoton, refPanel, estilo } = usePopoverAnclado({ ancho: ANCHO, alinear: 'derecha' });

  const utilizables = (acciones || []).filter(Boolean);
  const textoBoton = etiqueta || t('comun.abrirMenu');

  // Al cerrarse se olvida por dónde iba el teclado
  useEffect(() => { if (!abierto) setIndiceActivo(-1); }, [abierto]);

  const abrir = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (!abierto) alAbrir?.();
    alternar();
  };

  const ejecutar = (accion) => {
    if (accion.deshabilitada) return;
    cerrar();
    accion.onSelect?.();
  };

  const siguienteUtil = (desde, paso) => {
    for (let i = 1; i <= utilizables.length; i += 1) {
      const indice = (desde + paso * i + utilizables.length * 2) % utilizables.length;
      if (!utilizables[indice].deshabilitada) return indice;
    }
    return -1;
  };

  const alTeclear = (event) => {
    event.stopPropagation();
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        cerrar();
        break;
      case 'ArrowDown':
        event.preventDefault();
        setIndiceActivo((i) => siguienteUtil(i, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setIndiceActivo((i) => siguienteUtil(i === -1 ? 0 : i, -1));
        break;
      case 'Home':
        event.preventDefault();
        setIndiceActivo(siguienteUtil(-1, 1));
        break;
      case 'End':
        event.preventDefault();
        setIndiceActivo(siguienteUtil(0, -1));
        break;
      case 'Enter':
      case ' ':
        if (indiceActivo >= 0) {
          event.preventDefault();
          ejecutar(utilizables[indiceActivo]);
        }
        break;
      default:
        break;
    }
  };

  // El foco va al elemento activo para que el lector de pantalla lo anuncie
  useEffect(() => {
    if (!abierto || indiceActivo < 0) return;
    refPanel.current?.querySelectorAll('[role="menuitem"]')[indiceActivo]?.focus();
  }, [abierto, indiceActivo, refPanel]);

  if (utilizables.length === 0) return null;

  return (
    <>
      <button
        type="button"
        ref={refBoton}
        className={`menu-acciones-boton ${abierto ? 'ma-abierto' : ''}`}
        onClick={abrir}
        onKeyDown={(e) => { if (abierto) alTeclear(e); }}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={textoBoton}
        title={textoBoton}
      >
        <FiMoreVertical />
      </button>

      {abierto && estilo && createPortal(
        <div
          ref={refPanel}
          className="menu-acciones"
          role="menu"
          aria-label={titulo || textoBoton}
          style={estilo}
          onKeyDown={alTeclear}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {titulo && <p className="menu-acciones-titulo">{titulo}</p>}
          {utilizables.map((accion, indice) => (
            <button
              key={accion.clave}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={`menu-acciones-item ${accion.peligrosa ? 'peligrosa' : ''} ${accion.separada ? 'separada' : ''}`}
              disabled={accion.deshabilitada}
              onClick={() => ejecutar(accion)}
              onMouseEnter={() => setIndiceActivo(indice)}
            >
              {accion.icono && <span className="menu-acciones-icono">{accion.icono}</span>}
              <span className="menu-acciones-texto">
                {accion.etiqueta}
                {accion.descripcion && <small>{accion.descripcion}</small>}
              </span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

export default MenuAcciones;
